import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { EmailSection } from '../../types/email';

interface InPlaceEditableEmailPreviewProps {
  htmlContent: string;
  sections: EmailSection[];
  onContentChange: (sectionId: string, newContent: string) => void;
  onHtmlChange?: (newHtml: string) => void;
}

export const InPlaceEditableEmailPreview: React.FC<InPlaceEditableEmailPreviewProps> =
  ({ htmlContent, sections, onContentChange, onHtmlChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [originalContent, setOriginalContent] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const editingElementRef = useRef<HTMLElement | null>(null);

    // Make any clicked element editable
    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        if (isEditing) return;

        const target = event.target as HTMLElement;

        // Skip if clicking on links, buttons, or images
        if (
          target.tagName === 'A' ||
          target.tagName === 'BUTTON' ||
          target.tagName === 'IMG'
        ) {
          return;
        }

        // Find a text-containing element
        let editableElement = target;

        // If clicked on a container, find the closest text element
        if (
          !editableElement.textContent?.trim() ||
          editableElement.children.length > 0
        ) {
          // Look for the first child with text content
          const textElements = editableElement.querySelectorAll(
            'p, div, span, h1, h2, h3, h4, h5, h6, td, th, li',
          );
          for (let i = 0; i < textElements.length; i++) {
            const el = textElements[i] as HTMLElement;
            if (el.textContent?.trim() && el.children.length === 0) {
              editableElement = el;
              break;
            }
          }
        }

        // Check if element has text content and no child elements (to avoid editing containers)
        if (
          editableElement.textContent?.trim() &&
          editableElement.children.length === 0
        ) {
          event.preventDefault();
          event.stopPropagation();

          setIsEditing(true);
          setOriginalContent(editableElement.textContent);
          editingElementRef.current = editableElement;

          // Add editing styles
          editableElement.style.outline = '2px solid #1976d2';
          editableElement.style.backgroundColor = '#f5f5f5';
          editableElement.style.padding = '4px 8px';
          editableElement.style.borderRadius = '4px';
          editableElement.style.minHeight = '30px';
          editableElement.contentEditable = 'true';

          // Focus and select text
          setTimeout(() => {
            editableElement.focus();

            const range = document.createRange();
            range.selectNodeContents(editableElement);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }, 10);
        }
      },
      [isEditing],
    );

    // Save changes
    const saveChanges = useCallback(() => {
      if (editingElementRef.current && containerRef.current) {
        const newContent = editingElementRef.current.textContent || '';

        // Reset styles
        editingElementRef.current.style.outline = '';
        editingElementRef.current.style.backgroundColor = '';
        editingElementRef.current.style.padding = '';
        editingElementRef.current.style.borderRadius = '';
        editingElementRef.current.style.minHeight = '';
        editingElementRef.current.contentEditable = 'false';

        // Get the updated HTML from the container
        const htmlContainer = containerRef.current.querySelector(
          '#editable-html-container',
        );
        if (htmlContainer && onHtmlChange) {
          const updatedHtml = htmlContainer.innerHTML;
          onHtmlChange(updatedHtml);
        }

        // Call change handler with first section ID or default
        const sectionId = sections[0]?.id || 'default';
        onContentChange(sectionId, newContent);

        // Reset state
        setIsEditing(false);
        setOriginalContent('');
        editingElementRef.current = null;
      }
    }, [sections, onContentChange, onHtmlChange]);

    // Cancel editing
    const cancelEditing = useCallback(() => {
      if (editingElementRef.current) {
        // Restore original content
        editingElementRef.current.textContent = originalContent;

        // Reset styles
        editingElementRef.current.style.outline = '';
        editingElementRef.current.style.backgroundColor = '';
        editingElementRef.current.style.padding = '';
        editingElementRef.current.style.borderRadius = '';
        editingElementRef.current.style.minHeight = '';
        editingElementRef.current.contentEditable = 'false';

        // Reset state
        setIsEditing(false);
        setOriginalContent('');
        editingElementRef.current = null;
      }
    }, [originalContent]);

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (isEditing) {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            saveChanges();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing();
          }
        }
      },
      [isEditing, saveChanges, cancelEditing],
    );

    // Handle outside clicks
    const handleDocumentClick = useCallback(
      (event: Event) => {
        if (
          isEditing &&
          editingElementRef.current &&
          event.target instanceof Node &&
          !editingElementRef.current.contains(event.target as Node)
        ) {
          saveChanges();
        }
      },
      [isEditing, saveChanges],
    );

    // Add event listeners
    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleDocumentClick);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('click', handleDocumentClick);
      };
    }, [handleKeyDown, handleDocumentClick]);

    return (
      <Box
        ref={containerRef}
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          cursor: isEditing ? 'text' : 'pointer',
          background: 'none',
          '& p:hover, & div:hover, & span:hover, & h1:hover, & h2:hover, & h3:hover, & h4:hover, & h5:hover, & h6:hover, & td:hover, & th:hover, & li:hover':
            {
              backgroundColor: isEditing
                ? 'transparent'
                : 'rgba(25, 118, 210, 0.08)',
              borderRadius: '2px',
              transition: 'background-color 0.2s ease',
            },
          '& [contenteditable="true"]': {
            cursor: 'text !important',
            backgroundColor: 'rgba(25, 118, 210, 0.13)',
          },
        }}
      >
        <div
          id="editable-html-container"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{
            width: '100%',
            height: '100%',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            background: 'none',
          }}
        />

        {/* Editing indicator */}
        {isEditing && (
          <Box
            sx={{
              position: 'fixed',
              top: 10,
              right: 10,
              backgroundColor: 'primary.main',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1,
              fontSize: '12px',
              zIndex: 1000,
            }}
          >
            Editing... (Press Enter to save, Esc to cancel)
          </Box>
        )}
      </Box>
    );
  };
